import { IfAbility } from '@efdigital/ll-addon-auth';
import { Heading } from '@efdigital/ll-addon-ui';
import { ImpersonateUserModal } from '@efdigital/ll-admin-v2/components/users/impersonate-user-modal.gts';
import UserAccessSection from '@efdigital/ll-admin-v2/components/users/user-access-section.gts';
import UserActivitySection from '@efdigital/ll-admin-v2/components/users/user-activity-section.gts';
import UserIdentitySection from '@efdigital/ll-admin-v2/components/users/user-identity-section.gts';
import type { ManagementUser } from '@efdigital/ll-admin-v2/data/builders/users/find-user';
import type { LibraryUsersUserModel } from '@efdigital/ll-admin-v2/routes/library/users/user';
import {
  isManagementUser,
  profileDisplayName,
  profileEmailWithFallback,
} from '@efdigital/ll-admin-v2/utils/users';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { pageTitle } from 'ember-page-title';

interface Signature {
  Args: {
    model: LibraryUsersUserModel;
  };
}

export default class LibraryUsersUserTemplate extends Component<Signature> {
  get pageHeading(): string {
    if (this.args.model.profile) {
      return profileDisplayName(this.args.model.profile);
    }

    return this.args.model.userId;
  }

  get showDeletedBadge(): boolean {
    return Boolean(this.args.model.profile?.deleted);
  }

  get isBasicProfile(): boolean {
    return this.args.model.profileLevel === 'basic';
  }

  get managementProfile(): ManagementUser | null {
    const { profile } = this.args.model;
    if (profile && isManagementUser(profile)) {
      return profile;
    }

    return null;
  }

  <template>
    {{pageTitle this.pageHeading}}

    <div class="container py-5">
      <IfAbility @ability="clients:users" @atLeast="viewer">
        <:if>
          {{#if @model.loadError}}
            <div
              class="alert alert-danger"
              role="alert"
              data-test-user-load-error
            >
              {{@model.loadError}}
            </div>
          {{else if @model.profile}}

            <IfAbility @ability="impersonate" @atLeast="editor">
              <:if>
                <ImpersonateUserModal
                  @targetUserId={{@model.userId}}
                  @targetUserName={{profileEmailWithFallback @model.profile}}
                >
                  <:trigger as |showModal|>
                    <Heading
                      @text="User profile"
                      @secondary={{@model.profile.email}}
                    >

                      <LinkTo
                        @route="library.users.index"
                        class="btn btn-sm btn-outline-secondary"
                        data-test-user-back
                      >
                        <iconify-icon inline icon="ph:arrow-left" />
                        Back to search
                      </LinkTo>
                      <button
                        type="button"
                        class="btn btn-primary"
                        {{on "click" showModal}}
                        data-test-impersonation-open
                      >
                        Impersonate user...
                      </button>
                    </Heading>
                  </:trigger>
                </ImpersonateUserModal>
              </:if>
              <:else>

                <Heading
                  @text="User profile"
                  @secondary={{@model.profile.email}}
                >

                  <LinkTo
                    @route="library.users.index"
                    class="btn btn-sm btn-outline-secondary"
                    data-test-user-back
                  >
                    <iconify-icon inline icon="ph:arrow-left" />
                    Back to search
                  </LinkTo>
                </Heading>
              </:else>
            </IfAbility>

            <div class="row justify-content-center">
              <div class="col-lg-6">
                <UserIdentitySection @profile={{@model.profile}} />
                {{#if this.managementProfile}}
                  <UserActivitySection @profile={{this.managementProfile}} />
                {{/if}}
              </div>

              {{#if this.managementProfile}}
                <div class="col-lg-6">
                  <UserAccessSection @profile={{this.managementProfile}} />
                </div>
              {{/if}}

            </div>
          {{else}}
            <div class="alert alert-warning" role="alert">
              User profile could not be loaded.
            </div>
          {{/if}}
        </:if>
        <:else>
          <div class="alert alert-warning">
            You do not have permission to view user profiles.
          </div>
        </:else>
      </IfAbility>
    </div>
  </template>
}
